<template>
  <div>
    <div class="box term-row-compact" :class="{ 'box-50': is6MWT }">
      <div class="term w-auto">
        <div class="term-item term-item-lg">{{ $t('sessionsInfo.3595') }}</div>
        <div class="term-value">{{ startTime }}</div>
      </div>
      <div class="term w-auto">
        <div class="term-item term-item-lg">{{ $t('sessionsInfo.9239') }}</div>
        <div class="term-value">{{ endTime }}</div>
      </div>
      <div class="term w-auto">
        <div class="term-item term-item-lg">{{ $t('general.duration') }}:</div>
        <div class="term-value">{{ trainingDurationString }}</div>
      </div>
      <div class="term w-auto" id="averageStepRate">
        <div class="term-item">{{ $t('sessionsInfo.averagePace') }}:</div>
        <div class="term-value">
          {{ averageStepRate }}
        </div>
      </div>
      <div class="term w-auto" id="stepCount">
        <div class="term-item">{{ $t('sessionsInfo.3274') }}</div>
        <div class="term-value">{{ stepCountStr }}</div>
      </div>
      <div class="term w-auto">
        <div class="term-item term-item-xxl">{{ $t('sessionsInfo.borgScale') }}:</div>
        <div class="term-value">{{ borgScaleStr }}</div>
      </div>
      <div class="term w-auto">
        <div class="term-item term-item-xxl">{{ $t('sessionsInfo.maxHeartRate') }}:</div>
        <div class="term-value">
          {{ maxHeartRateStr }}
        </div>
      </div>
      <div class="term w-auto">
        <div class="term-item term-item-xxl">{{ $t('sessionsInfo.averageHeartRate') }}:</div>
        <div class="term-value">
          {{ averageHeartRateStr }}
        </div>
      </div>
      <div class="term w-auto">
        <div class="term-item term-item-xxl">
          {{ $t('sessionsInfo.5503') }}
        </div>
        <div class="term-value">{{ hrInZonePercentStr }}</div>
      </div>
    </div>

    <template v-if="commentFromPatient">
      <div class="canvas"></div>

      <div class="term">
        <div class="term-item term-item-full">
          {{ $t('sessionsInfo.comment') }}
        </div>
        <div class="term-value">{{ commentFromPatient }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import { DATE_FORMAT } from '@/main.config.js'
import('@/styles/components/terms.less')
import timeString from '@/modules/time-string.js'
import getAverageOfArray from '@/modules/average-of-array.js'
import { userRights } from '@/mixins/user-rights.js'

export default {
  name: 'TrainingResultsInfo',
  mixins: [userRights],
  inject: ['trainingStartDate', 'trainingEndDate', '$isEditableSession'],
  props: {
    stepCount: {
      type: Number,
      required: false
    },
    duration: {
      type: Number,
      required: true
    },
    stepRate: {
      type: Object,
      required: false
    },
    rhythmogram: {
      type: Object,
      required: false
    },
    hrMin: {
      type: Number,
      required: false
    },
    hrMax: {
      type: Number,
      required: false
    },
    borgScale: {
      type: Number,
      required: false
    },
    comment: {
      type: String,
      required: false
    },
    is6MWT: {
      type: Boolean,
      required: true
    },
    isActive: {
      type: Boolean,
      required: true
    }
  },
  mounted() {
    let width = window.innerWidth
    let averageStepRate = document.getElementById('averageStepRate').firstChild.classList
    let stepCount = document.getElementById('stepCount').firstChild.classList
    if (width >= 992 && !this.is6MWT) {
      averageStepRate.add('term-item-xxl')
      stepCount.add('term-item-xxl')
    } else {
      averageStepRate.add('term-item-lg')
      stepCount.add('term-item-lg')
    }

    window.addEventListener(
      'resize',
      () => {
        let averageStepRate = document.getElementById('averageStepRate').firstChild.classList
        let stepCount = document.getElementById('stepCount').firstChild.classList
        if (window.innerWidth >= 992 && !this.is6MWT) {
          averageStepRate.remove('term-item-lg')
          stepCount.remove('term-item-lg')
          averageStepRate.add('term-item-xxl')
          stepCount.add('term-item-xxl')
        } else {
          averageStepRate.remove('term-item-xxl')
          stepCount.remove('term-item-xxl')
          averageStepRate.add('term-item-lg')
          stepCount.add('term-item-lg')
        }
      },
      this
    )
  },
  computed: {
    hasRhythmogram() {
      return (
        !!this.rhythmogram &&
        Object.prototype.hasOwnProperty.call(this.rhythmogram, 'hrValues') &&
        this.rhythmogram.hrValues.length > 0
      )
    },
    hasStepRate() {
      return (
        !!this.stepRate &&
        Object.prototype.hasOwnProperty.call(this.stepRate, 'rateValues') &&
        this.stepRate.rateValues.length > 0
      )
    },
    trainingDurationString() {
      if (this.duration === 0) {
        return ' – '
      }

      return timeString(this.duration)
    },
    startTime() {
      if (!this.trainingStartDate) {
        return '–'
      }
      return this.trainingStartDate.format(DATE_FORMAT.timeHM)
    },
    endTime() {
      if (!this.trainingEndDate) {
        return '–'
      }
      return this.trainingEndDate.format(DATE_FORMAT.timeHM)
    },
    averageStepRate() {
      if (!this.hasStepRate) {
        return '–'
      }
      return `${getAverageOfArray(this.stepRate.rateValues)} ${this.$t('unit.sm')}`
    },
    maxHeartRateStr() {
      if (!this.hasRhythmogram) {
        return '–'
      }
      return `${Math.max(...this.rhythmogram.hrValues)} ${this.$t('unit.bpm')}`
    },
    averageHeartRateStr() {
      if (!this.hasRhythmogram) {
        return '–'
      }
      return `${getAverageOfArray(this.rhythmogram.hrValues)} ${this.$t('unit.bpm')}`
    },
    hrInZonePercentStr() {
      if (!this.hasRhythmogram) {
        return '–'
      }

      if (!this.hrMax || !this.hrMin) {
        return '100%'
      }

      const { hrValues, timeValues } = this.rhythmogram
      let timeOutOfZone = 0
      let currentOutOfZoneStart = null

      for (let i = 0; i < hrValues.length; i++) {
        const isOutOfZone = hrValues[i] > this.hrMax || hrValues[i] < this.hrMin

        if (isOutOfZone && !currentOutOfZoneStart) {
          currentOutOfZoneStart = timeValues[i]
        } else if (!isOutOfZone && currentOutOfZoneStart) {
          timeOutOfZone += timeValues[i] - currentOutOfZoneStart
          currentOutOfZoneStart = null
        }
      }

      if (currentOutOfZoneStart && !this.isActive) {
        timeOutOfZone += timeValues[timeValues.length - 1] - currentOutOfZoneStart
      }

      if (timeOutOfZone === 0 && !currentOutOfZoneStart) {
        return '100%'
      }

      return `${Math.floor((1 - timeOutOfZone / this.duration) * 100)}%`
    },
    commentFromPatient() {
      return this.comment
    },
    stepCountStr() {
      if (this.stepCount) return this.stepCount
      else return '–'
    },
    borgScaleStr() {
      if (this.borgScale) return this.borgScale
      else return '–'
    },
    isEditable() {
      return this.$isEditableSession()
    }
  }
}
</script>
